import React, { type MouseEvent, useState } from 'react';
import { createPortal } from 'react-dom';
import dynamic from 'next/dynamic';
import type { ProductVoucher } from '@/domainLogic/types/typesProduct.ts';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useTranslation } from '@/app/components/translations/Translation.tsx';
import { getVoucherText } from '@/app/components/search/components/Modal/Modals/VoucherModal/getVoucherText';
import { canUseDOM } from '@/app/common/utils/canUseDOM';
import AddElementToLastWordInString from '@/app/common/components/Utility/AddElementToTheEndOfString';
import { Icon } from '@/app/common/icons/Icon';
import { SuccessNearPriceBadgeContainer } from '../BaseBadge/styled';
import type { BadgeContext } from '../types';

const VoucherModal = dynamic(() => import('@/app/components/search/components/Modal/Modals/VoucherModal'), {
  ssr: false,
});

const VoucherNearPriceBadge = ({ dealCard }: BadgeContext) => {
  const { localization } = usePageWrapperData();
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);

  const voucherEnabled = localization?.configValuesApp?.voucherUspEnabled;
  const voucher = dealCard?.product?.promotions?.voucher as ProductVoucher | undefined;

  if (!voucherEnabled || !voucher?.code) return null;

  const text = getVoucherText(voucher, localization, t);
  if (!text) return null;

  const handleInfoClick = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setShowModal(true);
  };

  return (
    <>
      <SuccessNearPriceBadgeContainer as="button" type="button" onClick={handleInfoClick} aria-label={voucher.title}>
        <Icon category="Financial" name="icon-discount" size={20} color="iconSuccess" alt="" />
        <AddElementToLastWordInString text={text}>
          {/* Doubled so it beats the badge's `img { margin: 0 }` reset. */}
          <Icon
            category="Semantics"
            name="icon-circle-info"
            size={16}
            color="iconSuccess"
            alt=""
            css="&& { margin-left: 8px; }"
          />
        </AddElementToLastWordInString>
      </SuccessNearPriceBadgeContainer>
      {showModal &&
        canUseDOM &&
        createPortal(
          <VoucherModal isOpen={showModal} onClose={() => setShowModal(false)} voucher={voucher} />,
          document.body
        )}
    </>
  );
};

export default VoucherNearPriceBadge;
